import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import VoiceInputButton from './VoiceInputButton';

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
}

const ChatInput = ({ onSend, disabled }: ChatInputProps) => {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isListening, transcript, isSupported, startListening, stopListening, resetTranscript } =
    useSpeechRecognition();

  // Append voice transcript to text
  useEffect(() => {
    if (transcript) {
      setText(prev => prev + transcript);
      resetTranscript();
    }
  }, [transcript, resetTranscript]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 4 * 24; // ~4 lines
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, [text]);

  const handleSend = () => {
    if (!text.trim() || disabled) return;
    onSend(text);
    setText('');
    if (isListening) stopListening();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleVoice = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return (
    <div
      className="flex items-end gap-2 p-3 border-t transition-colors duration-200"
      style={{ borderColor: 'var(--border-color)' }}
    >
      <VoiceInputButton isListening={isListening} isSupported={isSupported} onToggle={toggleVoice} />
      <textarea
        ref={textareaRef}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Ask about Alexander..."
        disabled={disabled}
        rows={1}
        className="flex-1 resize-none rounded-lg px-3 py-2 text-sm outline-none transition-colors duration-200 border"
        style={{
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-primary)',
          borderColor: 'var(--border-color)',
        }}
      />
      <button
        type="button"
        onClick={handleSend}
        disabled={!text.trim() || disabled}
        className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white transition-all duration-200 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed hover:from-blue-600 hover:to-indigo-600"
        aria-label="Send message"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
        </svg>
      </button>
    </div>
  );
};

export default ChatInput;
